import configs from "../../lib/configs";
import {
    getBranding,
    getBrandingAndDashboard,
    getContentByCategory,
    getContentItemById,
    getDashboard,
    getStream,
    getWatchList,
    getWatchListUncategorized
} from "../../api/queries";
import posthog from "posthog-js";
import { refreshUserData, STORAGE_KEYS } from "../../lib/utils";
import { Storage } from "@lightningjs/sdk";
import widgets from "../../lib/widgets";
import { Vod } from "./Vod";
import { MovieSingle } from "./MovieSingle";
import { SeriesSingle } from "./SeriesSingle";
import { Playback } from "./Playback";
import { Favorite } from "./Favorite";
import { Trailer } from "./Trailer";
import { MediaCategoryName } from "../../components/data-display/MediaCategoryName/MediaCategoryName";
import { ExternalApp } from "../../lib/models";

const singleMediaWidgets = configs.layout.menu.fullWidth
    ? widgets
    : widgets.filter((w) => w !== <Lowercase<string>>"Menu");

export default [
    {
        path: "vod",
        component: Vod,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        let appsCategory
                        if (configs.modules.apps && window.Android && window.Android.getAppList) {
                            const apps = window.Android.getAppList();
                            appsCategory ={
                                id: "apps",
                                name: "Apps",
                                items: (typeof apps === "string" ? JSON.parse(apps) : apps).map((app: ExternalApp) => ({
                                    __typename: "ExternalApp",
                                    id: app.id,
                                    title: app.name,
                                    coverUrl: app.logo,
                                    backdropUrl: "",
                                    isFree: false
                                }))
                            };
                        }
                        // Batch branding and dashboard into a single GraphQL request for faster loading
                        if (configs.hasBranding) {
                            try {
                                const { branding, dashboard } = await getBrandingAndDashboard();

                                if(appsCategory) {
                                    dashboard.unshift(appsCategory)
                                }

                                page.branding = branding;
                                page.data = dashboard;
                                resolve();
                            } catch (batchError) {
                                console.log("Batch query failed, falling back to individual queries:", batchError);
                                // Fallback to parallel individual queries
                                const [brandingResult, dashboardResult] = await Promise.allSettled([
                                    getBranding().catch(() => null),
                                    getDashboard()
                                ]);

                                if (brandingResult.status === 'fulfilled' && brandingResult.value) {
                                    page.branding = brandingResult.value;
                                }

                                if (dashboardResult.status === 'fulfilled') {
                                    if(appsCategory) {
                                        dashboardResult.value.unshift(appsCategory)
                                    }
                                    page.data = dashboardResult.value;
                                    resolve();
                                } else {
                                    reject(dashboardResult.reason);
                                }
                            }
                        } else {
                            // No branding needed, just load dashboard
                            const dashboard = await getDashboard();
                            if(appsCategory) {
                                dashboard.unshift(appsCategory)
                            }
                            page.data = dashboard;
                            resolve();
                        }
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "movies",
        component: MediaCategoryName,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        // Load fewer items initially for faster first render, pagination will load more
                        page.data = await getContentByCategory("movie", 0, 20);
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "series",
        component: MediaCategoryName,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        page.showFilterCategories = false;
                        // Load fewer items initially for faster first render, pagination will load more
                        page.data = await getContentByCategory("series", 0, 20);
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "documentaries",
        component: MediaCategoryName,
        options: {
            clearHistory: true
        },
        on: (page: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        // Load fewer items initially for faster first render, pagination will load more
                        page.data = await getContentByCategory("documentary", 0, 20);
                        page.showFilterCategories = false;
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "category/:categoryId",
        component: MediaCategoryName,
        on: (page: any, { categoryId }: any) => {
            return new Promise<void>((resolve, reject) => {
                (async () => {
                    try {
                        if (categoryId === "apps" && window.Android?.getAppList) {
                            const apps = window.Android.getAppList()
                            page.data = {
                                id: "apps",
                                name: "Apps",
                                items: (typeof apps === "string" ? JSON.parse(apps) : apps).map((app: ExternalApp) => ({
                                    __typename: "ExternalApp",
                                    id: app.id,
                                    title: app.name,
                                    coverUrl: app.logo,
                                    backdropUrl: "",
                                    isFree: false
                                }))
                            };
                        } else {
                            page.data = await getContentByCategory(categoryId, 0, 20);
                        }
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        },
        widgets: widgets,
        cache: 60
    },
    {
        path: "movie/:movieId",
        component: MovieSingle,
        on: (page: any, { movieId }: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    page.data = await getContentItemById(movieId, "Movie");

                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        widgets: singleMediaWidgets
    },
    {
        path: "series/:movieId",
        component: SeriesSingle,
        on: (page: any, { movieId }: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    page.data = await getContentItemById(movieId, "TvSeries");

                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        widgets: widgets
    },
    {
        path: "playback/:movieId",
        component: Playback,
        on: (page: any, { movieId }: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    let data;
                    data = await getStream(movieId);
		    const user = Storage.get(STORAGE_KEYS.user);
                    posthog.capture("Playback", {
                        id: data.movie?.id || "",
                        title: data.movie?.title || "",
                        isVodafoneUser: user?.whiteListed || false,
                        whiteListed: user?.whiteListed || false
                    });

                    // data[0].url = "http://sample.vodobox.com/planete_interdite/planete_interdite_alternate.m3u8";
                    // data[0].url = "https://media.axprod.net/TestVectors/v7-Clear/Manifest_1080p.mpd";
                    // data[0].url = "https://livesim.dashif.org/dash/vod/testpic_2s/cea608.mpd";

                    // data[0].drm = undefined;
                    //     "https://test.playready.microsoft.com/service/rightsmanager.asmx?PlayRight=1&ContentKey=EAtsIJQPd5pFiRUrV9Layw==";

                    page.data = data;
                    resolve();
                } catch (e) {
                    if (navigator.onLine) {
                        await refreshUserData(true);

                        const user = Storage.get(STORAGE_KEYS.user);
                        if (!user?.subscribed) {
                            page.redirect = true;

                            resolve();
                        } else {
                            reject(e);
                        }
                    } else {
                        reject(e);
                    }
                }
            });
        },
        cache: 60
    },
    {
        path: "favorite",
        component: Favorite,
        on: (page: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    const isGridView =
                        ((configs as any).app?.layout?.bookmarks?.view ?? configs.layout?.bookmarks?.view) === "grid";
                    page.data = isGridView ? await getWatchListUncategorized() : await getWatchList();

                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        widgets: widgets
    },
    {
        path: "trailer",
        component: Trailer,
        on: (page: any) => {
            return new Promise<void>(async (resolve, reject) => {
                try {
                    page.data = window.selectedMovie?.trailer?.url;

                    resolve();
                } catch (e) {
                    console.log(e);
                    reject(e);
                }
            });
        }
    }
];
